import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const emptyForm = {
  code: '', description: '', discountType: 'percentage', value: '', minOrderAmount: '',
  maxDiscountAmount: '', usageLimit: '', perUserLimit: '1', startsAt: '', expiresAt: '',
  firstOrderOnly: false, isActive: true,
};

const dateInput = (value) => value ? new Date(value).toISOString().slice(0, 16) : '';

const getStatus = (promo) => {
  const now = Date.now();
  if (!promo.isActive) return ['Inactive', 'bg-gray-100 text-gray-700'];
  if (promo.startsAt && new Date(promo.startsAt).getTime() > now) return ['Scheduled', 'bg-blue-100 text-blue-800'];
  if (promo.expiresAt && new Date(promo.expiresAt).getTime() < now) return ['Expired', 'bg-red-100 text-red-800'];
  if (promo.usageLimit && promo.usedCount >= promo.usageLimit) return ['Limit reached', 'bg-amber-100 text-amber-800'];
  return ['Active', 'bg-emerald-100 text-emerald-800'];
};

export default function AdminPromoCodes() {
  const { hasPerm } = useAuth();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchPromos = () => {
    setLoading(true);
    api.get('/promos').then((res) => setPromos(res.data)).catch(() => toast.error('Could not load promo codes')).finally(() => setLoading(false));
  };

  useEffect(fetchPromos, []);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const startEdit = (promo) => {
    setEditingId(promo._id);
    setForm({
      code: promo.code,
      description: promo.description || '',
      discountType: promo.discountType,
      value: promo.value,
      minOrderAmount: promo.minOrderAmount || '',
      maxDiscountAmount: promo.maxDiscountAmount ?? '',
      usageLimit: promo.usageLimit ?? '',
      perUserLimit: promo.perUserLimit || 1,
      startsAt: dateInput(promo.startsAt),
      expiresAt: dateInput(promo.expiresAt),
      firstOrderOnly: Boolean(promo.firstOrderOnly),
      isActive: Boolean(promo.isActive),
    });
    setShowForm(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editingId) await api.put(`/promos/${editingId}`, form);
      else await api.post('/promos', form);
      toast.success(editingId ? 'Promo code updated' : 'Promo code created');
      setShowForm(false);
      fetchPromos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not save promo code');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (promo) => {
    if (!window.confirm(`Delete promo code ${promo.code}?`)) return;
    try {
      await api.delete(`/promos/${promo._id}`);
      toast.success('Promo code deleted');
      fetchPromos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not delete promo code');
    }
  };

  return (
    <div className="space-y-6 text-[#2B1810]">
      <header className="flex flex-col justify-between gap-4 border border-[#E8DFD8] bg-white p-6 shadow-sm sm:flex-row sm:items-center">
        <div><p className="text-[9px] font-bold uppercase text-[#9D772D]">Commerce</p><h1 className="mt-1 font-heading text-2xl font-bold">Promo Codes</h1><p className="mt-1 text-xs text-[#8A796C]">Create controlled discounts for checkout campaigns and customer offers.</p></div>
        {hasPerm('promo_codes', 'create') && <button type="button" onClick={startCreate} className="bg-[#C49A45] px-4 py-2.5 text-xs font-bold uppercase text-white hover:bg-[#B08736]">Add promo code</button>}
      </header>

      {showForm && (
        <form onSubmit={submit} className="border border-[#D9D0C8] bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-center justify-between border-b border-[#E8DFD8] pb-4"><div><h2 className="font-heading text-lg font-bold">{editingId ? 'Edit promo code' : 'New promo code'}</h2><p className="mt-1 text-[10px] text-[#8A796C]">Discounts are revalidated by the server when the order is placed.</p></div><button type="button" onClick={() => setShowForm(false)} className="h-9 w-9 text-[#8A796C] hover:text-red-700" aria-label="Close form"><svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6 6 18"/></svg></button></div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="label">Code<input required maxLength="30" className="input-field mt-1 uppercase" value={form.code} onChange={(e) => update('code', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))} placeholder="WELCOME10" /></label>
            <label className="label">Discount type<select className="input-field mt-1" value={form.discountType} onChange={(e) => update('discountType', e.target.value)}><option value="percentage">Percentage</option><option value="fixed">Fixed amount</option></select></label>
            <label className="label">Discount value<input required min="1" max={form.discountType === 'percentage' ? 100 : undefined} type="number" className="input-field mt-1" value={form.value} onChange={(e) => update('value', e.target.value)} /></label>
            <label className="label">Minimum order<input min="0" type="number" className="input-field mt-1" value={form.minOrderAmount} onChange={(e) => update('minOrderAmount', e.target.value)} placeholder="No minimum" /></label>
            <label className="label">Maximum discount<input min="1" type="number" className="input-field mt-1" value={form.maxDiscountAmount} onChange={(e) => update('maxDiscountAmount', e.target.value)} placeholder="No cap" /></label>
            <label className="label">Total usage limit<input min="1" type="number" className="input-field mt-1" value={form.usageLimit} onChange={(e) => update('usageLimit', e.target.value)} placeholder="Unlimited" /></label>
            <label className="label">Uses per customer<input required min="1" type="number" className="input-field mt-1" value={form.perUserLimit} onChange={(e) => update('perUserLimit', e.target.value)} /></label>
            <label className="label">Start date<input type="datetime-local" className="input-field mt-1" value={form.startsAt} onChange={(e) => update('startsAt', e.target.value)} /></label>
            <label className="label">Expiry date<input type="datetime-local" className="input-field mt-1" value={form.expiresAt} onChange={(e) => update('expiresAt', e.target.value)} /></label>
            <label className="label sm:col-span-2 lg:col-span-3">Internal description<input maxLength="240" className="input-field mt-1" value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="Campaign purpose or customer-facing offer note" /></label>
          </div>
          <div className="mt-5 flex flex-wrap gap-5 border-t border-[#E8DFD8] pt-4">
            <label className="flex items-center gap-2 text-xs font-semibold"><input type="checkbox" className="h-4 w-4 accent-[#C49A45]" checked={form.firstOrderOnly} onChange={(e) => update('firstOrderOnly', e.target.checked)} />First order only</label>
            <label className="flex items-center gap-2 text-xs font-semibold"><input type="checkbox" className="h-4 w-4 accent-[#C49A45]" checked={form.isActive} onChange={(e) => update('isActive', e.target.checked)} />Active</label>
          </div>
          <div className="mt-5 flex gap-3"><button type="submit" disabled={saving} className="bg-[#2B1810] px-6 py-2.5 text-xs font-bold uppercase text-white hover:bg-[#9D772D] disabled:opacity-60">{saving ? 'Saving...' : editingId ? 'Update code' : 'Create code'}</button><button type="button" onClick={() => setShowForm(false)} className="border border-[#D9D0C8] px-5 py-2.5 text-xs font-bold uppercase">Cancel</button></div>
        </form>
      )}

      <div className="overflow-x-auto border border-[#E8DFD8] bg-white shadow-sm">
        <table className="w-full min-w-[880px] text-left text-xs">
          <thead className="border-b border-[#E8DFD8] bg-[#F5EFEB] text-[10px] uppercase"><tr><th className="p-4">Code</th><th className="p-4">Discount</th><th className="p-4">Conditions</th><th className="p-4">Schedule</th><th className="p-4">Usage</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr></thead>
          <tbody className="divide-y divide-[#E8DFD8]">
            {loading ? <tr><td colSpan="7" className="p-8 text-center text-[#8A796C]">Loading promo codes...</td></tr> : promos.length === 0 ? <tr><td colSpan="7" className="p-8 text-center text-[#8A796C]">No promo codes created yet.</td></tr> : promos.map((promo) => {
              const [status, statusClass] = getStatus(promo);
              return <tr key={promo._id} className="hover:bg-[#FAF8F5]"><td className="p-4"><p className="font-mono text-sm font-bold text-[#7F5D1E]">{promo.code}</p><p className="mt-1 max-w-[190px] truncate text-[10px] text-[#8A796C]">{promo.description || 'No description'}</p></td><td className="p-4 font-bold">{promo.discountType === 'percentage' ? `${promo.value}%` : `₹${promo.value}`}{promo.maxDiscountAmount ? <p className="mt-1 text-[10px] font-normal text-[#8A796C]">Max ₹{promo.maxDiscountAmount}</p> : null}</td><td className="p-4 text-[#665950]"><p>Min ₹{promo.minOrderAmount || 0}</p><p>{promo.firstOrderOnly ? 'First order only' : `${promo.perUserLimit} per customer`}</p></td><td className="p-4 text-[#665950]"><p>{promo.startsAt ? new Date(promo.startsAt).toLocaleDateString() : 'Immediate'}</p><p>{promo.expiresAt ? `to ${new Date(promo.expiresAt).toLocaleDateString()}` : 'No expiry'}</p></td><td className="p-4 font-semibold">{promo.usedCount} / {promo.usageLimit || 'Unlimited'}</td><td className="p-4"><span className={`px-2 py-1 text-[9px] font-bold uppercase ${statusClass}`}>{status}</span></td><td className="p-4 text-right space-x-3">{hasPerm('promo_codes', 'edit') && <button type="button" onClick={() => startEdit(promo)} className="font-bold text-[#9D772D] hover:underline">Edit</button>}{hasPerm('promo_codes', 'delete') && <button type="button" onClick={() => remove(promo)} className="font-bold text-red-700 hover:underline">Delete</button>}</td></tr>;
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
